// 界面文案繁/简两套（NFR 4.1：繁體優先）。正文经文的繁简由后端 LLM 依 script 生成，
// 前端只切 UI chrome 文案与 <html lang/script>，避免引入重型客户端转换字典。
import type { Script } from "./types";

type Dict = Record<string, string>;

const HANT: Dict = {
  brand_sub: "佛學智慧檢索",
  new_chat: "＋ 新法談",
  topbar_title: "金剛般若 · 法談",
  menu_label: "歷史會話",
  refs_label: "經卷對照",
  refs_close: "關閉",
  script_toggle_title: "目前為繁體，點擊切換為簡體",
  refs_title: "經卷出處溯源",
  refs_empty: "問答之後，這裡逐條呈現所引經卷原文，供對照與校對。",
  ref_sample_label: "示例出處",
  ref_sample_1_cite: "金剛般若波羅蜜經 · 卷上",
  ref_sample_1_text: "「如來說第一波羅蜜，即非第一波羅蜜，是名第一波羅蜜。」",
  ref_sample_2_cite: "般若波羅蜜多心經",
  ref_sample_2_text: "「色不異空，空不異色；色即是空，空即是色。」",
  ref_sample_3_cite: "維摩詰所說經",
  ref_sample_3_text: "「若菩薩欲得淨土，當淨其心。」",
  placeholder: "請輸入您的提問，如「金剛經如何解釋『無我相』？」",
  send: "問",
  copy: "複製經文原句",
  share: "分享當前卡片",
  copied: "已複製",
  shared: "分享內容已複製，可貼上發送",
  regen: "重新生成",
  empty_title: "一念清淨，照見本來",
  empty_hint: "向私有佛典語料提問，回答必附經卷出處溯源。",
  ex1: "《金剛經》如何解釋「無我相」？",
  ex2: "《心經》中「色即是空」當作何解？",
  ex3: "唯識宗如何安立「阿賴耶識」？",
  net_reconnect: "網絡波動，正在靜默重連…",
  net_offline: "當前離線，已保留輸入與上下文",
  rate_limited: "提問過於頻繁，請稍候再試（每分鐘上限 15 次）",
  no_corpus: "知識庫尚無語料，請先於後台導入經卷。",
  session_empty: "尚無歷史法談",
  session_count_suffix: "則",
  delete_session: "刪除會話",
  delete_session_confirm: "確定刪除此會話？此操作不可恢復。",
  history_title: "歷史會話",
  classic_title: "常用經典",
  history_sample_1: "金剛經如何解釋「無我相」？",
  history_sample_2: "《心經》中「色即是空」",
  history_sample_3: "唯識宗如何安立「阿賴耶識」？",
  history_sample_4: "八正道的次第與實踐",
  classic_1: "金剛般若波羅蜜經",
  classic_2: "般若波羅蜜多心經",
  classic_3: "妙法蓮華經",
  classic_4: "大般涅槃經",
  classic_5: "阿含經典",
  classic_6: "唯識三十頌",
  school_prefix: "修學傾向",
  corpus_doc_label: "文獻",
  corpus_doc_unit: "部",
  corpus_word_label: "文字",
  corpus_label: "語料",
  voice_unsupported: "當前環境不支持語音輸入",
  voice_hint: "鬆開結束 · 上滑取消",
  voice_cancel: "鬆開取消",
  thinking: "正在參詳……",
  share_title: "［Zen-RAG］正在參詳",
};

const HANS: Dict = {
  brand_sub: "佛学智慧检索",
  new_chat: "＋ 新法谈",
  topbar_title: "金刚般若 · 法谈",
  menu_label: "历史会话",
  refs_label: "经卷对照",
  refs_close: "关闭",
  script_toggle_title: "当前为简体，点击切换为繁体",
  refs_title: "经卷出处溯源",
  refs_empty: "问答之后，这里逐条呈现所引经卷原文，供对照与校对。",
  ref_sample_label: "示例出处",
  ref_sample_1_cite: "金刚般若波罗蜜经 · 卷上",
  ref_sample_1_text: "「如来说第一波罗蜜，即非第一波罗蜜，是名第一波罗蜜。」",
  ref_sample_2_cite: "般若波罗蜜多心经",
  ref_sample_2_text: "「色不异空，空不异色；色即是空，空即是色。」",
  ref_sample_3_cite: "维摩诘所说经",
  ref_sample_3_text: "「若菩萨欲得净土，当净其心。」",
  placeholder: "请输入您的提问，如「金刚经如何解释『无我相』？」",
  send: "问",
  copy: "复制经文原句",
  share: "分享当前卡片",
  copied: "已复制",
  shared: "分享内容已复制，可粘贴发送",
  regen: "重新生成",
  empty_title: "一念清净，照见本来",
  empty_hint: "向私有佛典语料提问，回答必附经卷出处溯源。",
  ex1: "《金刚经》如何解释「无我相」？",
  ex2: "《心经》中「色即是空」当作何解？",
  ex3: "唯识宗如何安立「阿赖耶识」？",
  net_reconnect: "网络波动，正在静默重连…",
  net_offline: "当前离线，已保留输入与上下文",
  rate_limited: "提问过于频繁，请稍候再试（每分钟上限 15 次）",
  no_corpus: "知识库尚无语料，请先于后台导入经卷。",
  session_empty: "尚无历史法谈",
  session_count_suffix: "则",
  delete_session: "删除会话",
  delete_session_confirm: "确定删除此会话？此操作不可恢复。",
  history_title: "历史会话",
  classic_title: "常用经典",
  history_sample_1: "金刚经如何解释「无我相」？",
  history_sample_2: "《心经》中「色即是空」",
  history_sample_3: "唯识宗如何安立「阿赖耶识」？",
  history_sample_4: "八正道的次第与实践",
  classic_1: "金刚般若波罗蜜经",
  classic_2: "般若波罗蜜多心经",
  classic_3: "妙法莲华经",
  classic_4: "大般涅槃经",
  classic_5: "阿含经典",
  classic_6: "唯识三十颂",
  school_prefix: "修学倾向",
  corpus_doc_label: "文献",
  corpus_doc_unit: "部",
  corpus_word_label: "文字",
  corpus_label: "语料",
  voice_unsupported: "当前环境不支持语音输入",
  voice_hint: "松开结束 · 上滑取消",
  voice_cancel: "松开取消",
  thinking: "正在参详……",
  share_title: "［Zen-RAG］正在参详",
};

let current: Script = "zh-Hant";

export function setScript(s: Script): void {
  current = s;
  document.documentElement.lang = s === "zh-Hans" ? "zh-Hans" : "zh-Hant";
  document.documentElement.dataset.script = s;
  document.body.dataset.script = s;
}

export function t(key: keyof typeof HANT): string {
  return (current === "zh-Hans" ? HANS : HANT)[key] ?? HANT[key] ?? key;
}

export function scriptLabel(): string {
  return current === "zh-Hans" ? "简" : "繁";
}

export function scriptToggleTitle(): string {
  return t("script_toggle_title" as keyof typeof HANT);
}
